import { create } from 'zustand'
import { projectApi } from '@/api/projectApi'
import { ApiError } from '@/api/httpClient'
import type { CreateProjectRequest, Project, ProjectListItem } from '@/api/types'

interface ProjectsStore {
  projects: ProjectListItem[]
  loading: boolean
  error: string | null

  loadProjects: (workspaceId: number) => Promise<void>
  createProject: (workspaceId: number, body: CreateProjectRequest) => Promise<ProjectListItem>
  insertProject: (project: Project | ProjectListItem) => ProjectListItem
  softDelete: (projectId: number) => Promise<void>
  rename: (projectId: number, name: string) => Promise<ProjectListItem>
  transferProject: (projectId: number, targetWorkspaceId: number) => Promise<Project>
  reset: () => void
}

function toProjectListItem(project: Project | ProjectListItem): ProjectListItem {
  return {
    id: project.id,
    name: project.name,
    dialect: project.dialect,
    current_commit_id: project.current_commit_id,
    workspace: project.workspace,
    created_at: project.created_at,
    modified_at: project.modified_at,
    current_commit: project.current_commit,
  }
}

export const useProjectsStore = create<ProjectsStore>((set) => ({
  projects: [],
  loading: false,
  error: null,

  async loadProjects(workspaceId) {
    set({ loading: true, error: null })
    try {
      const page = await projectApi.list(workspaceId, { expand: 'current_commit', limit: 100 })
      set({ projects: page.results, loading: false })
    } catch (err) {
      set({ loading: false, error: errMsg(err) })
    }
  },

  async createProject(workspaceId, body) {
    const project = await projectApi.create(workspaceId, body)
    const item = toProjectListItem(project)
    set((s) => ({ projects: [item, ...s.projects] }))
    return item
  },

  insertProject(project) {
    const item = toProjectListItem(project)
    set((s) => ({
      projects: s.projects.some((existing) => existing.id === item.id)
        ? s.projects
        : [item, ...s.projects],
    }))
    return item
  },

  async softDelete(projectId) {
    await projectApi.patch(projectId, { is_active: false })
    set((s) => ({ projects: s.projects.filter((p) => p.id !== projectId) }))
  },

  async rename(projectId, name) {
    const updated = await projectApi.patch(projectId, { name }, { silent: true })
    const item = toProjectListItem(updated)
    set((s) => ({ projects: s.projects.map((p) => (p.id === projectId ? item : p)) }))
    return item
  },

  async transferProject(projectId, targetWorkspaceId) {
    const transferred = await projectApi.transfer(projectId, targetWorkspaceId)
    set((s) => ({ projects: s.projects.filter((p) => p.id !== projectId) }))
    return transferred
  },

  reset() {
    set({ projects: [], loading: false, error: null })
  },
}))

function errMsg(err: unknown): string {
  if (err instanceof ApiError) return err.message
  if (err instanceof Error) return err.message
  return 'Erro desconhecido'
}
